import React, { useState, useEffect } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectValue,
  SelectTrigger,
} from "./ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

type Tone = {
  label: string;
  description: string;
};

// Storage key for persisting tone selection
const STORAGE_KEY = "lastSelectedTone";
const CUSTOM_MODE_KEY = "customToneMode";

// Tone mapping with labels and descriptions
const toneOptions = [
  {
    label: "Persuasive",
    description:
      "Persuasive - Harness the art of compelling rhetoric to sway even the most steadfast minds. This isn't mere persuasion; it's an intellectual symphony that harmonizes logic and emotion, leaving no room for dissent. Craft arguments so irresistible that opposition feels futile.",
  },
  {
    label: "Sarcastic",
    description:
      "Sarcastic - Wield razor-sharp wit to slice through pretension with biting irony. Every remark is a masterstroke of mockery, exposing absurdities while keeping a clever smirk. This isn't just sarcasm; it's a sophisticated game of verbal chess where every move leaves an indelible mark.",
  },
  {
    label: "Shitposting",
    description:
      "Shitposting - Unleash a maelstrom of absurdity and irreverence, turning convention on its head with mischievous glee. This isn't simple trolling; it's a calculated chaos that disrupts norms and provokes reactions. Your words become a whirlwind of satire and unpredictability, leaving others both baffled and intrigued.",
  },
  {
    label: "Objective",
    description:
      "Objective - Present facts with unwavering clarity and impartiality. This isn't just neutrality; it's a beacon of rationality that cuts through bias like a knife through fog. Deliver insights with such precision that the truth becomes undeniable.",
  },
  {
    label: "Analytical",
    description:
      "Analytical - Delve deep into data and theories with surgical precision. No assumption is safe from your scrutiny as you deconstruct and examine every component. This is analysis on overdrive—revealing insights that transform understanding and challenge preconceptions.",
  },
  {
    label: "Informative",
    description:
      "Informative - Serve as an inexhaustible source of knowledge, enlightening your audience with a wealth of information. There's no room for ambiguity—only a torrent of clarity that quenches the thirst for understanding and leaves no question unanswered.",
  },
  {
    label: "Explanatory",
    description:
      "Explanatory - Skillfully unravel complex ideas, transforming the convoluted into the comprehensible. This isn't just explaining; it's demystifying the enigmatic until enlightenment dawns effortlessly. Guide your audience through labyrinths of thought with ease and grace.",
  },
  {
    label: "Critical",
    description:
      "Critical - Offer penetrating evaluations with unflinching honesty. It's not merely about pointing out flaws; it's about delivering incisive analysis that cuts to the core. Your critiques are thorough explorations that leave no stone unturned, fostering deeper insight and improvement.",
  },
  {
    label: "Comparative",
    description:
      "Comparative - Masterfully juxtapose ideas to highlight striking contrasts and illuminating parallels. This isn't just comparison; it's an insightful exploration that reveals hidden connections and sharp distinctions, enriching the audience's perspective in profound ways.",
  },
  {
    label: "Speculative",
    description:
      "Speculative - Venture boldly into the realm of the unknown, proposing daring hypotheses that ignite the imagination. This isn't mere conjecture; it's a fearless leap into possibilities that challenge the boundaries of conventional thought.",
  },
  {
    label: "Reflective",
    description:
      "Reflective - Engage in deep introspection, examining ideas and experiences with profound thoughtfulness. This isn't casual pondering; it's a contemplative journey that uncovers hidden truths and invites others to explore the depths of understanding alongside you.",
  },
  {
    label: "Narrative",
    description:
      "Narrative - Weave compelling stories that captivate and transport your audience to new worlds. It's not just storytelling; it's an immersive experience filled with vivid imagery and emotion that leaves a lasting imprint on the imagination.",
  },
  {
    label: "Humorous",
    description:
      "Humorous - Infuse your words with wit and playful charm, eliciting laughter and delight. It's not just about cracking jokes; it's about brightening minds with clever humor that entertains while engaging the intellect.",
  },
  {
    label: "Optimistic",
    description:
      "Optimistic - Radiate positivity and hope, highlighting the best in every situation. This isn't mere cheerfulness; it's an inspiring vision that uplifts spirits and encourages others to see possibilities where they might not have before.",
  },
  {
    label: "Pessimistic",
    description:
      "Pessimistic - Offer a stark perspective that doesn't shy away from potential pitfalls and harsh realities. This isn't negativity for its own sake; it's a candid appraisal that strips away illusions, compelling others to confront the challenges head-on.",
  },
  {
    label: "Conspiracist",
    description:
      "Conspiracist - Peel back the layers of accepted narratives to uncover hidden connections and unseen forces. This isn't simple doubt; it's a deep dive into the shadows where intrigue and mystery intertwine. Invite others to question the obvious and explore the possibilities that lurk beneath the surface.",
  },
] satisfies Tone[];

// Utility function to get a random tone from the list
export const getRandomTone = (): Tone => {
  const randomIndex = Math.floor(Math.random() * toneOptions.length);
  return toneOptions[randomIndex];
};

// Format custom tone to match built-in tones format
const formatCustomTone = (text: string): string => {
  if (!text) return "Custom - Create your own tone";
  return text.startsWith("Custom - ") ? text : `Custom - ${text}`;
};

// Check if a tone is a custom one
const isCustomTone = (description: string): boolean => {
  if (!description) return false;
  return (
    description.startsWith("Custom - ") ||
    !toneOptions.some((option) => option.description === description)
  );
};

// Extract custom tone text without the prefix
const extractCustomText = (description: string): string => {
  return description.startsWith("Custom - ")
    ? description.substring(9)
    : description;
};

// Save value to localStorage with error handling
const saveToStorage = (key: string, value: string): void => {
  try {
    localStorage.setItem(key, value);
  } catch (_) {
    console.warn(_);
    // Silently ignore storage errors
  }
};

export default function ToneSelector({
  className,
  tone,
  onToneChange,
}: {
  className?: string;
  tone: Tone["description"];
  onToneChange: (tone: Tone["description"]) => void;
}) {
  const [isCustomMode, setIsCustomMode] = useState(isCustomTone(tone));
  const [customInput, setCustomInput] = useState(extractCustomText(tone));
  const [initialLoad, setInitialLoad] = useState(true);

  // On mount, try to load the persisted tone and mode
  useEffect(() => {
    if (!initialLoad) return;

    try {
      // Load saved tone
      const savedTone = localStorage.getItem(STORAGE_KEY);
      if (savedTone && savedTone !== tone) {
        onToneChange(savedTone);
      }

      // Load saved mode
      const savedMode = localStorage.getItem(CUSTOM_MODE_KEY);
      if (savedMode !== null) {
        setIsCustomMode(savedMode === "true");
      } else {
        setIsCustomMode(isCustomTone(tone));
      }

      setInitialLoad(false);
    } catch (_) {
      // Silently ignore storage errors
      console.warn(_);

      setInitialLoad(false);
    }
  }, [initialLoad, tone, onToneChange]);

  // When tone changes externally
  useEffect(() => {
    if (initialLoad) return;

    if (isCustomTone(tone)) {
      setCustomInput(extractCustomText(tone));
    }
  }, [tone, initialLoad]);

  const getCurrentLabel = (): string => {
    // If in custom mode, use a placeholder
    if (isCustomMode) return "";

    // Find the matching predefined option or use the first one
    const option = toneOptions.find((opt) => opt.description === tone);
    return option?.label || toneOptions[0].label;
  };

  const handleToneChange = (label: string): void => {
    if (isCustomMode) return; // Skip if in custom mode

    const selectedOption = toneOptions.find((opt) => opt.label === label);
    if (selectedOption) {
      onToneChange(selectedOption.description);
      saveToStorage(STORAGE_KEY, selectedOption.description);
    }
  };

  const handleCustomInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    const value = e.target.value;
    setCustomInput(value);
    const formattedTone = formatCustomTone(value);
    onToneChange(formattedTone);
    saveToStorage(STORAGE_KEY, formattedTone);
  };

  const toggleCustomMode = (): void => {
    const newMode = !isCustomMode;
    setIsCustomMode(newMode);

    saveToStorage(CUSTOM_MODE_KEY, String(newMode));

    // When switching to custom, initialize with existing custom content
    if (newMode) {
      if (!isCustomTone(tone)) {
        const formattedTone = formatCustomTone("");
        onToneChange(formattedTone);
        saveToStorage(STORAGE_KEY, formattedTone);
      }
    } else {
      // When switching back to preset, use the first tone if none is selected
      if (isCustomTone(tone)) {
        onToneChange(toneOptions[0].description);
        saveToStorage(STORAGE_KEY, toneOptions[0].description);
      }
    }
  };

  return (
    <div className={cn("flex flex-col gap-1", className)}>
      <div className="flex items-center mb-1"></div>

      <div className="flex gap-2 flex-wrap">
        <div className={cn("w-full md:w-96", isCustomMode && "hidden")}>
          <Select value={getCurrentLabel()} onValueChange={handleToneChange}>
            <SelectTrigger id="tone" className="w-full">
              <SelectValue placeholder="Select tone" />
            </SelectTrigger>

            <SelectContent className="z-[1301]">
              {toneOptions.map((option) => (
                <SelectItem key={option.label} value={option.label}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {isCustomMode ? (
          <div className="flex items-center gap-2 flex-wrap">
            <Input
              type="text"
              value={customInput}
              onChange={handleCustomInputChange}
              placeholder="Describe your custom tone style... (e.g., 'Technical expert with deep domain knowledge')"
              className="flex-grow w-full md:w-96"
            />
            <Button
              variant="outline"
              size="sm"
              className="shrink-0"
              onClick={toggleCustomMode}
            >
              Use Preset
            </Button>
          </div>
        ) : (
          <Button
            variant="outline"
            size="sm"
            className="self-center"
            onClick={toggleCustomMode}
          >
            Custom Tone
          </Button>
        )}
      </div>
    </div>
  );
}
